import axios from 'axios';

export interface ServicePermission {
  id: number;
  service_id: number;
  user_guid: string;
  can_read: boolean;
  can_execute: boolean;
  created_at: string;
  user_name?: string;
  user_email?: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

class PermissionService {
  async getUserPermissions(userGuid: string): Promise<string[]> {
    try {
      const response = await axios.get<ApiResponse<{ id: number }[]>>('/api/services');

      if (!response.data.success || !response.data.data) {
        return [];
      }

      const serviceIds = response.data.data.map(s => s.id);
      const permissions: string[] = [];

      for (const serviceId of serviceIds) {
        const permResponse = await axios.get<ApiResponse<ServicePermission[]>>(
          `/api/services/${serviceId}/permissions`
        );

        if (permResponse.data.success && permResponse.data.data) {
          const userPerm = permResponse.data.data.find(p => p.user_guid === userGuid);
          if (userPerm) {
            permissions.push(String(serviceId));
          }
        }
      }

      return permissions;
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      throw error;
    }
  }

  async setUserPermissions(userGuid: string, queryIds: string[]): Promise<void> {
    try {
      const response = await axios.get<ApiResponse<{ id: number }[]>>('/api/services');

      if (!response.data.success || !response.data.data) {
        throw new Error('Erro ao buscar servicos');
      }

      const allServiceIds = response.data.data.map(s => s.id);

      for (const serviceId of allServiceIds) {
        const hasPermission = queryIds.includes(String(serviceId));

        if (hasPermission) {
          await axios.post(`/api/services/${serviceId}/permissions`, {
            user_guid: userGuid,
            can_read: true,
            can_execute: true
          });
        } else {
          try {
            await axios.delete(`/api/services/${serviceId}/permissions/${userGuid}`);
          } catch (error) {
            if (error && error.response?.status === 404) {
              continue;
            }
            throw error;
          }
        }
      }
    } catch (error) {
      console.error('Error setting user permissions:', error);
      throw error;
    }
  }

  async getPermissionsCount(userGuids: string[]): Promise<Map<string, number>> {
    const counts = new Map<string, number>();
    userGuids.forEach(guid => counts.set(guid, 0));

    if (userGuids.length === 0) return counts;

    try {
      const response = await axios.get<ApiResponse<{ id: number }[]>>('/api/services');

      if (!response.data.success || !response.data.data) {
        return counts;
      }

      for (const service of response.data.data) {
        const permResponse = await axios.get<ApiResponse<ServicePermission[]>>(
          `/api/services/${service.id}/permissions`
        );

        if (permResponse.data.success && permResponse.data.data) {
          for (const perm of permResponse.data.data) {
            if (userGuids.includes(perm.user_guid)) {
              const current = counts.get(perm.user_guid) || 0;
              counts.set(perm.user_guid, current + 1);
            }
          }
        }
      }

      return counts;
    } catch (error) {
      console.error('Error fetching permissions count:', error);
      return counts;
    }
  }

  async getServiceUsers(queryId: string): Promise<string[]> {
    try {
      const response = await axios.get<ApiResponse<ServicePermission[]>>(
        `/api/services/${queryId}/permissions`
      );

      if (!response.data.success || !response.data.data) {
        return [];
      }

      return response.data.data.map(p => p.user_guid);
    } catch (error) {
      console.error('Error fetching service users:', error);
      throw error;
    }
  }

  async getServicePermissions(serviceId: string): Promise<ServicePermission[]> {
    try {
      const response = await axios.get<ApiResponse<ServicePermission[]>>(
        `/api/services/${serviceId}/permissions`
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Erro ao buscar permissoes');
      }

      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching service permissions:', error);
      throw error;
    }
  }

  async addServicePermission(
    serviceId: string,
    userGuid: string,
    canRead: boolean = true,
    canExecute: boolean = true
  ): Promise<void> {
    try {
      const response = await axios.post<ApiResponse<unknown>>(
        `/api/services/${serviceId}/permissions`,
        {
          user_guid: userGuid,
          can_read: canRead,
          can_execute: canExecute
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Erro ao adicionar permissao');
      }
    } catch (error) {
      console.error('Error adding service permission:', error);
      throw error;
    }
  }

  async removeServicePermission(serviceId: string, userGuid: string): Promise<void> {
    try {
      await axios.delete(`/api/services/${serviceId}/permissions/${userGuid}`);
    } catch (error) {
      if (error && error.response?.status === 404) {
        return;
      }
      console.error('Error removing service permission:', error);
      throw error;
    }
  }
}

export const permissionService = new PermissionService();
